<template>
  <div class="dda-table">
    <v-card-title class="toolbar primary white--text mb-0 pb-3">
      <div>
        <span class="headline">
          <h1 class="headline ml-2">
            <v-icon dark>description</v-icon>
            Digital Depot Accounts
          </h1>
        </span>
      </div>
    </v-card-title>
    <v-card-title class="toolbar primary white--textn pt-0">
      <v-text-field
        v-model="searchValue"
        append-icon="search"
        label="search value"
        single-line
        flat
        dark
        color="primary"
        solo-inverted
        hide-details
        class="ml-2 mr-2"
      />
      <v-tooltip
        v-if="searchValue"
        top
        dark
      >
        <template v-slot:activator="{ on }">
          <v-btn
            color="secondary"
            fab
            dark
            absolute
            bottom
            left
            @click.stop="onRefresh"
            v-on="on"
          >
            <v-icon>refresh</v-icon>
          </v-btn>
        </template>
        <span>Refresh</span>
      </v-tooltip>
    </v-card-title>

    <!-- DATA TABLE -->
    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :total-items.sync="ddAccountsPagination.total"
      :rows-per-page-items="rowsPerPageItems"
      :loading="isLoading"
      loading-text="Loading..."
      disable-initial-sort
      :pagination.sync="pagination"
      @update:pagination="updatePagination"
    >
      <!-- DDA DATA TABLE ROW START -->
      <template
        slot="items"
        slot-scope="props"
      >
        <tr
          :class="getRowClasses(props)"
          @click="toggleSelected(props.item)"
        >
          <td class="">
            <DataChip
              :data="props.item.id"
            />
          </td>
          <td>
            <UserChip
              :id="props.item.accountId"
            />
          </td>
          <td>{{ props.item.operatingLicense }}</td>
          <td>{{ props.item.brand }}</td>
          <td>{{ props.item.status }}</td>
          <td>
            <DateDisplay
              :date="props.item.timestamp"
              :show-time="true"
            />
          </td>
        </tr>
        <tr v-if="props.item.id && selected === props.item.id">
          <td colspan="6">
            <v-container
              fluid
              cols="12"
              class="d-flex my-0"
            >
              <v-card
                v-if="props.item.mangoPay"
                class="my-0"
              >
                <v-card-title class="toolbar purple white--text subheading">
                  <span>Mangopay Settings</span>
                </v-card-title>
                <v-flex
                  class="pa-2"
                  xs12
                >
                  <MangopayChip
                    :id="props.item.mangoPay ? props.item.mangoPay.providerIdentifier : null"
                    :wallet-id="props.item.mangoPay ? props.item.mangoPay.walletId : null"
                    :iban="props.item.mangoPay && props.item.mangoPay.walletDetails ? props.item.mangoPay.walletDetails.iban : null"
                    :bic="props.item.mangoPay && props.item.mangoPay.walletDetails ? props.item.mangoPay.walletDetails.bic : null"
                  />
                </v-flex>
              </v-card>
            </v-container>
            <v-container
              fluid
              cols="12"
              class="d-flex my-0"
            >
              <v-card
                v-if="props.item.vault"
                class="my-0"
              >
                <v-card-title class="toolbar success white--text subheading">
                  <span>Vault Settings</span>
                </v-card-title>
                <v-flex
                  class="pa-2"
                  xs12
                >
                  <VaultChip
                    :wallet-id="props.item.vault.walletId ? props.item.vault.walletId : null"
                    :wallet-address="props.item.vault.walletAddress ? props.item.vault.walletAddress : null"
                    :username="props.item.vault.username ? props.item.vault.username : null"
                  />
                </v-flex>
              </v-card>
            </v-container>
            <v-container
              fluid
              cols="12"
              class="d-flex my-0 pt-0"
            >
              <v-card
                class="my-0"
              >
                <v-card-title class="toolbar info white--text subheading">
                  <span>Event History</span>
                </v-card-title>
                <v-flex
                  class="pa-2"
                  xs12
                >
                  <v-data-table
                    :headers="historyHeaders"
                    :rows-per-page-items="rowsPerPageItems"
                    :items="props.item.history"
                    :pagination="historyPagination"
                    :total-items="props.item.history.length"
                    disable-initial-sort
                  >
                    <template
                      slot="items"
                      slot-scope="historyProps"
                    >
                      <tr>
                        <td class="">
                          {{ historyProps.item.id }}
                        </td>
                        <td class="">
                          {{ historyProps.item.status }}
                        </td>
                        <td class="text-xs">
                          {{ (new Date(historyProps.item.timestamp)).toISOString() }}
                        </td>
                        <td>
                          <v-expansion-panel popout>
                            <v-expansion-panel-content>
                              <template v-slot:header>
                                <div>DebugInfo</div>
                              </template>
                              <v-card-text light>
                                <code
                                  class="d-block w-100 text-xs-left"
                                >{{ historyProps.item }}
                                </code>
                              </v-card-text>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-flex>
              </v-card>
            </v-container>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import _ from 'lodash';
import DigitalDepotAccountSearchKeys from '../../enum/digitalDepots/DigitalDepotAccountSearchKeys';
import DateDisplay from '@/components/payoutsAndTaxes/payout/shared/DateDisplay';
import UserChip from '@/components/shared/user/UserChip';
import DataChip from '@/components/shared/DataChip';
import MangopayChip from '@/components/shared/mangopay/MangopayChip';
import VaultChip from '@/components/shared/vault/VaultChip';

export default {
  name: 'DigitalDepotAccountsTable',
  components: {
    VaultChip,
    DateDisplay,
    UserChip,
    DataChip,
    MangopayChip,
  },
  data() {
    return {
      currentDda: null,
      tableData: [],
      isLoading: false,
      isHistory: false,
      selected: 0,
      isEditing: false,
      isRecalculate: false,
      recalculateUserId: '',
      pagination: {
        page: this.$store.state.strategiesStore.ddAccountsPagination.page,
        rowsPerPage: this.$store.state.strategiesStore.ddAccountsPagination.rowsPerPage,
        sortBy: 'timestamp',
        descending: true,
      },
      rowsPerPageItems: [
        {
          text: '10',
          value: 10,
        },
        {
          text: '25',
          value: 25,
        },
        {
          text: '50',
          value: 50,
        },
        {
          text: '100',
          value: 100,
        },
      ],
      historyPagination: {
        page: 1,
        rowsPerPage: 10,
        sortBy: 'timestamp',
        descending: true,
      },
      searchKey: null,
      searchValue: '',
      filterString: '',
      historyHeaders: [
        { text: 'Id', value: 'id' },
        { text: 'Status', value: 'status' },
        { text: 'Updated At', value: 'timestamp' },
        { text: 'Data', value: 'Data' },
      ],
      headers: [
        { text: 'ID', value: 'id' },
        { text: 'UserId', value: 'account_id' },
        { text: 'Type', value: 'operatingLicense' },
        { text: 'Brand', value: 'brand' },
        { text: 'Status', value: 'Status' },
        { text: 'Updated At', value: 'timestamp' },
      ],
    };
  },
  computed: {
    ddAccounts() {
      return this.$store.state.strategiesStore.ddAccounts;
    },
    ddAccountsPagination() {
      return this.$store.state.strategiesStore.ddAccountsPagination;
    },
    digitalDepotAccountSearchKeys() {
      return Object.keys(DigitalDepotAccountSearchKeys).map(key => DigitalDepotAccountSearchKeys[key]);
    },
    filteredItems() {
      const { filterString } = this;

      return this.ddAccounts;
    },
  },
  watch: {
    searchValue() {
      const query = {
        page: 1,
        rowsPerPage: this.pagination.rowsPerPage,
      };
      this.$store.dispatch('setDdAccountsPagination', query);
      this.reloadDigitalDepotAccounts(query);
    },
  },
  mounted() {
    this.reloadDigitalDepotAccounts = _.debounce(this.reloadDigitalDepotAccounts, 500);
  },
  methods: {
    async reloadDigitalDepotAccounts(query) {
      const search = this.searchValue;

      this.isLoading = true;
      try {
        await this.$store.dispatch('fetchDdAccounts', { ...query, ...(search && { search }) });
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
      }
    },
    getRowClasses(props) {
      const classes = [];
      if (this.isDeleted(props)) {
        classes.push('row-danger');
      }

      return classes.join(' ');
    },
    isDeleted(props) {
      if (props && props.item && props.item.history && props.item.history.length === 0) {
        return false;
      }

      return props.item.history[props.item.history.length - 1]._topic.param_3 === 'deleted';
    },
    updatePagination(pagination) {
      const query = {
        page: pagination.page, rowsPerPage: pagination.rowsPerPage,
      };
      this.$store.dispatch('setDdAccountsPagination', query);
      this.reloadDigitalDepotAccounts(query);
    },
    onRefresh() {
      this.reloadDigitalDepotAccounts(query);
    },
    toggleEdit() {
      this.isEditing = !this.isEditing;
    },
    toggleHistory() {
      this.isHistory = !this.isHistory;
    },
    toggleSelected(dda) {
      this.selected = this.selected === dda.id ? null : dda.id;
    },
    toggleRecalculate() {
      this.isRecalculate = !this.isRecalculate;
    },
    onShowHistory(item) {
      this.isHistory = true;
      this.currentDda = item;
    },
    onShowEdit(item) {
      this.isEditing = true;
      this.currentDda = item;
    },
    onShowRecalculate(userId) {
      this.isRecalculate = true;
      this.recalculateUserId = userId;
    },
  },
};
</script>
<style lang="scss">
  .row-danger {
    background-color: #f44336;
  }
</style>



// WEBPACK FOOTER //
// src/components/digitalDepots/DdAccountsTable.vue